import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { SimpleService } from '../types';

export interface NicheServiceTemplate {
  id: string;
  name: string;
  type: 'fixed' | 'hourly';
  description?: string;
  niche: string;
}

interface DbServiceTemplate {
  id: string;
  nicho: string;
  nome_servico: string;
  tipo: string;
  descricao?: string;
}

interface UseNicheServiceTemplatesProps {
  userNiche: string;
  existingServices: Pick<SimpleService, 'name'>[];
  userId: string;
}

function normalizeString(str: string): string {
  return str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

function getDismissKey(userId: string, niche: string): string {
  return `dismissed_service_suggestions_${userId}_${normalizeString(niche)}`;
}

export function useNicheServiceTemplates({
  userNiche,
  existingServices,
  userId,
}: UseNicheServiceTemplatesProps) {
  const [allTemplates, setAllTemplates] = useState<NicheServiceTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [dismissedIds, setDismissedIds] = useState<Set<string>>(() => {
    try {
      const key = getDismissKey(userId, userNiche);
      const raw = localStorage.getItem(key);
      return new Set<string>(raw ? JSON.parse(raw) : []);
    } catch {
      return new Set<string>();
    }
  });

  const dismissSuggestion = useCallback((id: string) => {
    setDismissedIds(prev => {
      const next = new Set(prev).add(id);
      try {
        localStorage.setItem(getDismissKey(userId, userNiche), JSON.stringify([...next]));
      } catch { /* localStorage indisponível */ }
      return next;
    });
  }, [userId, userNiche]);

  const fetchTemplates = useCallback(async (isCancelled?: () => boolean) => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.from('niche_service_templates').select('*');
      if (isCancelled?.()) return;
      if (error) throw error;
      if (data) {
        setAllTemplates((data as DbServiceTemplate[]).map(item => ({
          id: item.id,
          name: item.nome_servico,
          type: item.tipo === 'hourly' ? 'hourly' : 'fixed',
          description: item.descricao || undefined,
          niche: item.nicho,
        })));
      }
    } catch (err) {
      console.error('useNicheServiceTemplates error:', err);
    } finally {
      if (!isCancelled?.()) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    let ignore = false;
    fetchTemplates(() => ignore);
    return () => { ignore = true; };
  }, [fetchTemplates]);

  const suggestions = useMemo<NicheServiceTemplate[]>(() => {
    if (!allTemplates.length) return [];

    const cleanNiche = normalizeString(userNiche);
    const existingNamesSet = new Set(existingServices.map(s => normalizeString(s.name)));

    // Filtra por nicho exato + nicho "Geral (Todos os Nichos)" sempre incluso
    return allTemplates.filter(t => {
      const tNiche = normalizeString(t.niche);
      const matchesNiche = tNiche === cleanNiche || tNiche === normalizeString('Geral (Todos os Nichos)');
      return matchesNiche && !existingNamesSet.has(normalizeString(t.name)) && !dismissedIds.has(t.id);
    });
  }, [allTemplates, userNiche, existingServices, dismissedIds]);

  return { suggestions, isLoading, dismissSuggestion };
}
